// @ts-nocheck
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useActiveProfile, getActiveProfileId } from '@/hooks/useActiveProfile';

interface DashboardData {
  tasks: any[];
  colleges: any[];
  events: any[];
  profile: any;
  isLoading: boolean;
  error: string | null;
}

/**
 * Optimized hook that fetches all dashboard data in a single batch
 * to reduce the number of database queries and improve performance
 */
export const useOptimizedDashboardData = (profileId?: string) => {
  const { profile, user } = useActiveProfile();
  const [data, setData] = useState<DashboardData>({
    tasks: [],
    colleges: [],
    events: [],
    profile: null,
    isLoading: true,
    error: null
  });
  
  // Get target profile ID using centralized logic
  const targetProfileId = getActiveProfileId() || profileId || profile?.id;
  
  console.log('🔍 useOptimizedDashboardData - targetProfileId:', targetProfileId);

  const fetchAllData = useMemo(() => async () => {
    if (!targetProfileId || !user) return;

    try {
      setData(prev => ({ ...prev, isLoading: true, error: null }));

      // Batch all queries to run in parallel for better performance
      const [
        tasksResult,
        collegesResult,
        eventsResult,
        profileResult
      ] = await Promise.allSettled([
        // Tasks query - simplified
        supabase
          .from('recruiting_tasks')
          .select('id, title, status, due_date')
          .eq('profile_id', targetProfileId)
          .limit(10),
        
        // Saved colleges - using correct table name
        supabase
          .from('saved_colleges')
          .select('id, college_id, status')
          .eq('profile_id', targetProfileId)
          .limit(10),
        
        // Events - using correct column name (start_date instead of event_date)
        supabase
          .from('calendar_events')
          .select('id, title, start_date')
          .eq('profile_id', targetProfileId)
          .gte('start_date', new Date().toISOString())
          .order('start_date', { ascending: true })
          .limit(5),
        
        // Profile data - always fetch when we have a targetProfileId different from current profile
        targetProfileId && targetProfileId !== profile?.id ? 
          supabase
            .from('profiles')
            .select('id, name, active_membership_slug')
            .eq('id', targetProfileId)
            .single() :
          Promise.resolve({ data: profile, error: null })
      ]);

      setData({
        tasks: tasksResult.status === 'fulfilled' ? (tasksResult.value.data || []) : [],
        colleges: collegesResult.status === 'fulfilled' ? (collegesResult.value.data || []) : [],
        events: eventsResult.status === 'fulfilled' ? (eventsResult.value.data || []) : [],
        profile: profileResult.status === 'fulfilled' ? profileResult.value.data : profile,
        isLoading: false,
        error: null
      });

    } catch (error) {
      setData(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to load dashboard data'
      }));
    }
  }, [targetProfileId, user, profile, profileId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return data;
};
